import { z } from 'zod'
import { COUNTRIES } from '../countries'
import { RELEASE_GATED_KINDS } from '../kinds'
import { pageQuerySchema } from '../pagination'
import { hasControlChars, isHttpUrl, isIsoDate, isValidIsin, revisionSchema } from '../validators'
import {
  CONFIDENCE_LEVELS,
  DATE_PRECISIONS,
  DIALOGUE_EVENT_TYPES,
  ENGAGEMENT_STATUSES,
  EVENT_TYPES,
  FINDINGS,
  INVOLVEMENT_TYPES,
  ISSUER_IDENTIFIER_TYPES,
  MAPPING_STATES,
  PRIORITIES,
  REDISTRIBUTION,
  REVIEW_STATES,
  type ReviewState,
  RIGHTS_BASIS,
  SECTORS,
  SECURITY_IDENTIFIER_TYPES,
  SEVERITIES,
  SHARING,
  SOURCE_CLASSES,
  STAGES,
  THEMES,
} from '../vocab'

/**
 * Records API contracts (M2/M3, DM-010..021, VAL-001..066). One schema per kind is shared by
 * the API and the workspace forms (P-UI-008): the field limits below are the single source of
 * truth, and the forms both read them (`maxLength`) and run the same zod schema before submit.
 *
 * Rules that need the database (references, issuer consistency, published prerequisites,
 * staff roles, uniqueness) live in `apps/api/src/services/validation`.
 */

/** Kinds served by `/api/v1/records/:kind` (frozen route contract, lane B). */
export const RECORD_API_KINDS = [
  'issuers',
  'securities',
  'sources',
  'incidents',
  'assessments',
  'engagements',
  'objectives',
  'milestones',
  'dialogues',
] as const
export type RecordApiKind = (typeof RECORD_API_KINDS)[number]

export function isRecordApiKind(value: string): value is RecordApiKind {
  return (RECORD_API_KINDS as readonly string[]).includes(value)
}

/** Kinds with the submit -> approve -> publish workflow through this API (minutes: lane E). */
export const WORKFLOW_KINDS = ['assessments', 'milestones', 'dialogues'] as const
export type WorkflowKind = (typeof WORKFLOW_KINDS)[number]

export function isWorkflowKind(value: string): value is WorkflowKind {
  return (WORKFLOW_KINDS as readonly string[]).includes(value)
}

/** Kinds released through `/release` (D-06). */
export const RELEASE_KINDS = RELEASE_GATED_KINDS
export type ReleaseKind = (typeof RELEASE_KINDS)[number]

export function isReleaseKind(value: string): value is ReleaseKind {
  return (RELEASE_KINDS as readonly string[]).includes(value)
}

/** Kinds that carry contributors (P-WF-001) and review events. */
export const CONTRIBUTED_KINDS = [...WORKFLOW_KINDS, ...RELEASE_KINDS] as const

/**
 * Server-controlled fields (DM-043): never accepted in a create or update body. The case
 * reviewer assignment is `assigned_reviewer_id`, so there is no exception to make.
 */
export const CONTROLLED_FIELDS = [
  'id',
  'kind',
  'state',
  'version',
  'previous_id',
  'author',
  'reviewer',
  'review_note',
  'created_by',
  'created_at',
  'updated_at',
  'archived_at',
  'submitted_by',
  'submitted_at',
  'submission_note',
  'approved_by',
  'approved_at',
  'approval_reason',
  'published_by',
  'published_at',
  'publication_reason',
  'returned_by',
  'returned_at',
  'return_reason',
  'retracted_by',
  'retracted_at',
  'retraction_reason',
  'released_by',
  'released_at',
  'closed_at',
  'closed_by',
  'assessment_version',
  'search_name',
] as const

/** VAL-044 / VAL-045 messages. */
export const CONTROLLED_FIELD_MESSAGES = {
  create: 'Workflow metadata is assigned by the server.',
  update: 'Use the review workflow to change this field.',
} as const

// --- Limits (P-UI-008) --------------------------------------------------------

export interface FieldLimit {
  min: number
  max: number
}

const L = (min: number, max: number): FieldLimit => ({ min, max })

/** VAL-005: free text is at most 12,000 characters unless a tighter limit applies. */
export const TEXT_MAX = 12000
/** VAL-007: list references hold at most 100 ids. */
export const REFS_MAX = 100
/** WF-006: review reasons are 8-12,000 characters; submission notes up to 2,000. */
export const REVIEW_REASON = L(8, 12000)
export const SUBMISSION_NOTE_MAX = 2000

export const RECORD_LIMITS = {
  issuers: {
    legal_name: L(2, 300),
    alias: L(1, 300),
    aliases: L(0, 20),
    description: L(0, 4000),
    website: L(0, 2048),
    identifier_value: L(1, 64),
    identifiers: L(0, 10),
  },
  securities: {
    identifier_value: L(1, 64),
    name: L(0, 300),
    mapping_source: L(1, 300),
  },
  sources: {
    title: L(1, 500),
    url: L(1, 2048),
    summary: L(1, TEXT_MAX),
    licence_ref: L(0, 300),
    rights_terms: L(0, 4000),
    independence_group: L(0, 120),
  },
  incidents: {
    title: L(1, 500),
    location: L(1, 300),
    legal_status: L(1, 300),
    description: L(1, TEXT_MAX),
    company_response: L(1, TEXT_MAX),
    uncertainty: L(1, TEXT_MAX),
    entity_role: L(0, 300),
    event_identity: L(0, 300),
    authority: L(0, 300),
    proceeding_reference: L(0, 300),
  },
  assessments: {
    norm: L(1, 2000),
    remediation: L(1, TEXT_MAX),
    rationale: L(1, TEXT_MAX),
    change_reason: L(1, TEXT_MAX),
  },
  engagements: {
    title: L(1, 300),
    next_action: L(1, 2000),
    closure_reason: L(0, 4000),
  },
  objectives: {
    title: L(1, 300),
    baseline: L(1, TEXT_MAX),
    change_request: L(1, TEXT_MAX),
    next_action: L(1, 2000),
  },
  milestones: {
    rationale: L(1, TEXT_MAX),
    limitations: L(1, TEXT_MAX),
  },
  dialogues: {
    title: L(1, 300),
    company_response: L(1, TEXT_MAX),
    fruuit_judgment: L(1, TEXT_MAX),
    next_action: L(1, 2000),
  },
} as const satisfies Record<RecordApiKind, Record<string, FieldLimit>>

// --- Field builders (VAL-003..014) ---------------------------------------------

const REQUIRED = 'This field is required.'

function typeError(expected: string) {
  return (issue: { input?: unknown }) =>
    issue.input === undefined || issue.input === null ? REQUIRED : expected
}

/** Required trimmed text: VAL-003 (present, not empty), VAL-005, VAL-006, VAL-012. */
export function requiredText(limit: FieldLimit) {
  return z
    .string({ error: typeError('Must be text.') })
    .max(limit.max, { error: `Must be at most ${limit.max} characters.` })
    .refine((v) => !hasControlChars(v), { error: 'Control characters are not permitted.' })
    .transform((v) => v.trim())
    .refine((v) => v.length >= Math.max(limit.min, 1), {
      error: limit.min > 1 ? `Enter at least ${limit.min} characters.` : REQUIRED,
    })
}

/** Optional text: absent, null or blank all store null (FORM-006). */
export function optionalText(limit: FieldLimit) {
  return z
    .string({ error: 'Must be text.' })
    .max(limit.max, { error: `Must be at most ${limit.max} characters.` })
    .refine((v) => !hasControlChars(v), { error: 'Control characters are not permitted.' })
    .transform((v) => v.trim() || null)
    .nullable()
    .optional()
}

/** VAL-013: a real YYYY-MM-DD date. */
export const requiredDate = z
  .string({ error: typeError('Use an ISO date (YYYY-MM-DD).') })
  .refine(isIsoDate, { error: 'Use an ISO date (YYYY-MM-DD).' })

/** DM-034: optional dates accept null (unknown), distinct from absent. Blank means null. */
export const optionalDate = z
  .string({ error: 'Use an ISO date (YYYY-MM-DD).' })
  .refine((v) => v === '' || isIsoDate(v), { error: 'Use an ISO date (YYYY-MM-DD).' })
  .transform((v) => v || null)
  .nullable()
  .optional()

/** VAL-008: a record id; blank means "none" on optional references. */
export const requiredRef = z
  .string({ error: typeError('Must be a record ID.') })
  .min(1, { error: REQUIRED })
  .max(64, { error: 'Must be a record ID.' })

export const optionalRef = z
  .string({ error: 'Must be a record ID.' })
  .max(64, { error: 'Must be a record ID.' })
  .transform((v) => v || null)
  .nullable()
  .optional()

/** VAL-007: up to 100 record ids; duplicates are dropped. */
export const refList = z
  .array(z.string().min(1).max(64), { error: 'Must be an array of record IDs.' })
  .max(REFS_MAX, { error: `At most ${REFS_MAX} references.` })
  .transform((ids) => [...new Set(ids)])

function enumField<const T extends readonly [string, ...string[]]>(values: T, message: string) {
  return z.enum(values, { error: (issue) => (issue.input === undefined ? REQUIRED : message) })
}

const httpUrl = z
  .string({ error: typeError('Source URL must use http or https.') })
  .max(2048, { error: 'Must be at most 2048 characters.' })
  .refine(isHttpUrl, { error: 'Source URL must use http or https.' })

type Ctx = z.RefinementCtx
function issue(ctx: Ctx, path: string, message: string) {
  ctx.addIssue({ code: 'custom', path: [path], message })
}

// --- Per-kind field shapes -----------------------------------------------------

const issuerIdentifier = z.strictObject({
  type: enumField(ISSUER_IDENTIFIER_TYPES.values, 'Invalid identifier type.'),
  value: requiredText(RECORD_LIMITS.issuers.identifier_value),
})

export const issuerFields = {
  legal_name: requiredText(RECORD_LIMITS.issuers.legal_name),
  country: enumField(COUNTRIES.values, 'Use an ISO 3166 country code.'),
  sector: enumField(SECTORS.values, 'Invalid sector.'),
  aliases: z
    .array(requiredText(RECORD_LIMITS.issuers.alias))
    .max(RECORD_LIMITS.issuers.aliases.max, { error: 'At most 20 aliases.' })
    .optional(),
  description: optionalText(RECORD_LIMITS.issuers.description),
  website: z
    .string({ error: 'Must be text.' })
    .max(2048)
    .refine((v) => v === '' || isHttpUrl(v), { error: 'Website must use http or https.' })
    .transform((v) => v || null)
    .nullable()
    .optional(),
  parent_issuer_id: optionalRef,
  status: z.enum(['active', 'inactive'], { error: 'Invalid issuer status.' }).optional(),
  identifiers: z
    .array(issuerIdentifier)
    .max(RECORD_LIMITS.issuers.identifiers.max, { error: 'At most 10 identifiers.' })
    .optional(),
}

export const securityFields = {
  identifier_type: enumField(SECURITY_IDENTIFIER_TYPES.values, 'Invalid identifier type.'),
  identifier_value: requiredText(RECORD_LIMITS.securities.identifier_value),
  name: optionalText(RECORD_LIMITS.securities.name),
  issuer_id: optionalRef,
  mapping_state: enumField(MAPPING_STATES.values, 'Invalid mapping state.'),
  mapping_source: requiredText(RECORD_LIMITS.securities.mapping_source),
  effective_from: requiredDate,
  effective_to: optionalDate,
}

export const sourceFields = {
  client_org_id: optionalRef,
  issuer_id: optionalRef,
  title: requiredText(RECORD_LIMITS.sources.title),
  url: httpUrl,
  source_class: enumField(SOURCE_CLASSES.values, 'Invalid source class.'),
  retrieved_at: requiredDate,
  publication_date: optionalDate,
  publication_date_precision: enumField(DATE_PRECISIONS.values, 'Invalid date precision.')
    .nullable()
    .optional(),
  reporting_period_start: optionalDate,
  reporting_period_end: optionalDate,
  rights_basis: enumField(RIGHTS_BASIS.values, 'Invalid rights basis.'),
  redistribution: enumField(REDISTRIBUTION.values, 'Invalid redistribution value.'),
  licence_ref: optionalText(RECORD_LIMITS.sources.licence_ref),
  rights_terms: optionalText(RECORD_LIMITS.sources.rights_terms),
  sharing: enumField(SHARING.values, 'sharing must be client or internal.'),
  summary: requiredText(RECORD_LIMITS.sources.summary),
  independence_group: optionalText(RECORD_LIMITS.sources.independence_group),
}

export const incidentFields = {
  client_org_id: optionalRef,
  issuer_id: requiredRef,
  title: requiredText(RECORD_LIMITS.incidents.title),
  location: requiredText(RECORD_LIMITS.incidents.location),
  country: enumField(COUNTRIES.values, 'Use an ISO 3166 country code.').nullable().optional(),
  event_type: enumField(EVENT_TYPES.values, 'Invalid event type.').nullable().optional(),
  theme: enumField(THEMES.values, 'Invalid theme.').nullable().optional(),
  legal_status: requiredText(RECORD_LIMITS.incidents.legal_status),
  description: requiredText(RECORD_LIMITS.incidents.description),
  company_response: requiredText(RECORD_LIMITS.incidents.company_response),
  uncertainty: requiredText(RECORD_LIMITS.incidents.uncertainty),
  entity_role: optionalText(RECORD_LIMITS.incidents.entity_role),
  event_date: optionalDate,
  event_date_precision: enumField(DATE_PRECISIONS.values, 'Invalid date precision.')
    .nullable()
    .optional(),
  event_identity: optionalText(RECORD_LIMITS.incidents.event_identity),
  authority: optionalText(RECORD_LIMITS.incidents.authority),
  proceeding_reference: optionalText(RECORD_LIMITS.incidents.proceeding_reference),
  legal_entity_id: optionalRef,
  status_as_of: optionalDate,
  previous_status_id: optionalRef,
  source_ids: refList.optional(),
}

export const assessmentFields = {
  client_org_id: optionalRef,
  issuer_id: requiredRef,
  finding: enumField(FINDINGS.values, 'Invalid assessment finding or priority.'),
  norm: requiredText(RECORD_LIMITS.assessments.norm),
  involvement: enumField(INVOLVEMENT_TYPES.values, 'Invalid involvement.'),
  severity: enumField(SEVERITIES.values, 'Invalid severity.'),
  confidence: enumField(CONFIDENCE_LEVELS.values, 'Invalid confidence.'),
  remediation: requiredText(RECORD_LIMITS.assessments.remediation),
  rationale: requiredText(RECORD_LIMITS.assessments.rationale),
  priority: enumField(PRIORITIES.values, 'Invalid assessment finding or priority.'),
  effective_date: optionalDate,
  change_reason: requiredText(RECORD_LIMITS.assessments.change_reason),
  incident_ids: refList.optional(),
  source_ids: refList.optional(),
}

export const engagementFields = {
  client_org_id: optionalRef,
  issuer_id: requiredRef,
  assessment_id: requiredRef,
  title: requiredText(RECORD_LIMITS.engagements.title),
  theme: enumField(THEMES.values, 'Invalid theme.').nullable().optional(),
  assigned_manager_id: requiredRef,
  assigned_reviewer_id: requiredRef,
  status: enumField(ENGAGEMENT_STATUSES.values, 'Invalid engagement status.').optional(),
  next_action: requiredText(RECORD_LIMITS.engagements.next_action),
  due_date: requiredDate,
  original_start: optionalDate,
  fruuit_start: optionalDate,
  closure_reason: optionalText(RECORD_LIMITS.engagements.closure_reason),
}

export const objectiveFields = {
  engagement_id: requiredRef,
  title: requiredText(RECORD_LIMITS.objectives.title),
  category: enumField(THEMES.values, 'Invalid category.').nullable().optional(),
  baseline: requiredText(RECORD_LIMITS.objectives.baseline),
  change_request: requiredText(RECORD_LIMITS.objectives.change_request),
  owner_id: requiredRef,
  next_action: requiredText(RECORD_LIMITS.objectives.next_action),
  due_date: requiredDate,
}

export const milestoneFields = {
  objective_id: requiredRef,
  stage: enumField(STAGES.values, 'Invalid objective stage.'),
  evidence_ids: refList.optional(),
  rationale: requiredText(RECORD_LIMITS.milestones.rationale),
  limitations: requiredText(RECORD_LIMITS.milestones.limitations),
  assessment_date: requiredDate,
}

export const dialogueFields = {
  engagement_id: requiredRef,
  event_date: requiredDate,
  event_type: enumField(DIALOGUE_EVENT_TYPES.values, 'Invalid correspondence type.'),
  title: requiredText(RECORD_LIMITS.dialogues.title),
  company_response: requiredText(RECORD_LIMITS.dialogues.company_response),
  fruuit_judgment: requiredText(RECORD_LIMITS.dialogues.fruuit_judgment),
  substantive: z.boolean({ error: 'substantive must be true or false.' }).optional(),
  next_action: requiredText(RECORD_LIMITS.dialogues.next_action),
  due_date: requiredDate,
  source_ids: refList.optional(),
}

// --- Record-level rules that need no database ---------------------------------

type Loose = Record<string, unknown>
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

const RULES: Record<RecordApiKind, (v: Loose, ctx: Ctx) => void> = {
  issuers: () => {},
  securities: (v, ctx) => {
    const value = str(v.identifier_value)
    if (v.identifier_type === 'ISIN' && value !== null) {
      if (!/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(value)) {
        issue(
          ctx,
          'identifier_value',
          'ISIN must contain two letters, nine alphanumeric characters and a check digit.',
        ) // VAL-016
      } else if (!isValidIsin(value)) {
        issue(ctx, 'identifier_value', 'ISIN check digit is invalid. Use OTHER for a non-ISIN.') // VAL-017
      }
    }
    if (v.mapping_state === 'verified' && !str(v.issuer_id)) {
      issue(ctx, 'issuer_id', 'Verified mapping needs an issuer.') // VAL-019
    }
    const from = str(v.effective_from)
    const to = str(v.effective_to)
    if (from && to && to < from) {
      issue(ctx, 'effective_to', 'Effective-to must not precede effective-from.') // VAL-060
    }
  },
  sources: (v, ctx) => {
    const start = str(v.reporting_period_start)
    const end = str(v.reporting_period_end)
    if (start && end && start > end) {
      issue(ctx, 'reporting_period_start', 'Reporting-period start must not be later than its end.') // VAL-023
    }
    const published = str(v.publication_date)
    const retrieved = str(v.retrieved_at)
    if (published && retrieved && published > retrieved.slice(0, 10)) {
      issue(ctx, 'publication_date', 'Publication date must not follow the retrieval date.') // VAL-062
    }
    if (v.sharing === 'client' && v.redistribution !== 'allowed') {
      issue(
        ctx,
        'sharing',
        'Only sources whose rights allow redistribution can be shared with clients.',
      ) // GAP-DD-004
    }
  },
  incidents: (v, ctx) => {
    const identity = ['event_identity', 'authority', 'proceeding_reference', 'legal_entity_id']
    const all = [...identity, 'status_as_of']
    const anySet = all.some((f) => str(v[f])) || str(v.previous_status_id)
    if (anySet && !all.every((f) => str(v[f]))) {
      issue(
        ctx,
        all.find((f) => !str(v[f])) ?? 'event_identity',
        'A dated proceeding record needs event identity, authority, proceeding reference, legal entity and status-as-of date together.',
      ) // VAL-024
    }
    const eventDate = str(v.event_date)
    const asOf = str(v.status_as_of)
    if (eventDate && asOf && eventDate > asOf) {
      issue(ctx, 'event_date', 'The event date must not follow the status-as-of date.') // VAL-062
    }
  },
  assessments: (v, ctx) => {
    const incidents = Array.isArray(v.incident_ids) ? v.incident_ids : []
    if (v.finding === 'Substantiated norms concern' && incidents.length === 0) {
      issue(ctx, 'incident_ids', 'An adverse finding needs incident evidence.') // VAL-034
    }
  },
  engagements: (v, ctx) => {
    if (v.status === 'closed' && !str(v.closure_reason)) {
      issue(ctx, 'closure_reason', 'A closed case needs a closure reason.') // VAL-038
    }
    if (str(v.assigned_manager_id) && v.assigned_manager_id === v.assigned_reviewer_id) {
      issue(ctx, 'assigned_reviewer_id', 'Manager and reviewer must be different people.') // VAL-039
    }
    const original = str(v.original_start)
    const fruuit = str(v.fruuit_start)
    if (original && fruuit && original > fruuit) {
      issue(ctx, 'fruuit_start', 'Fruuit coverage cannot start before the original engagement.') // VAL-062
    }
  },
  objectives: () => {},
  milestones: (v, ctx) => {
    const evidence = Array.isArray(v.evidence_ids) ? v.evidence_ids : []
    if (typeof v.stage === 'string' && /^S[34]/.test(v.stage) && evidence.length === 0) {
      issue(ctx, 'evidence_ids', 'Implementation and outcome stages require evidence.') // VAL-041
    }
  },
  dialogues: () => {},
}

export const RECORD_FIELDS = {
  issuers: issuerFields,
  securities: securityFields,
  sources: sourceFields,
  incidents: incidentFields,
  assessments: assessmentFields,
  engagements: engagementFields,
  objectives: objectiveFields,
  milestones: milestoneFields,
  dialogues: dialogueFields,
} as const

/**
 * Fields that a PATCH may not change: parents and identity (a different issuer or case is a
 * different record; P-ID-014 client context is fixed at creation). Incident legal history is
 * also fixed (WF-013), but that answers 409, so it is checked by the API, not the schema.
 */
export const IMMUTABLE_FIELDS: Record<RecordApiKind, readonly string[]> = {
  issuers: ['identifiers'],
  securities: ['identifier_type', 'identifier_value'],
  sources: ['client_org_id'],
  incidents: ['client_org_id', 'issuer_id'],
  assessments: ['client_org_id', 'issuer_id'],
  engagements: ['client_org_id', 'issuer_id'],
  objectives: ['engagement_id'],
  milestones: ['objective_id'],
  dialogues: ['engagement_id'],
}

/** WF-013: legal-history fields never change after an incident is created (409). */
export const INCIDENT_LEGAL_HISTORY_FIELDS = [
  'legal_entity_id',
  'event_date',
  'previous_status_id',
  'authority',
  'proceeding_reference',
  'legal_status',
  'event_identity',
  'status_as_of',
] as const

type FieldsOf<K extends RecordApiKind> = (typeof RECORD_FIELDS)[K]

function createSchemaFor<K extends RecordApiKind>(kind: K) {
  const shape = RECORD_FIELDS[kind] as FieldsOf<K>
  return z.strictObject(shape).superRefine((v, ctx) => RULES[kind](v as Loose, ctx))
}

function omit<T extends Record<string, z.ZodType>>(shape: T, keys: readonly string[]) {
  return Object.fromEntries(Object.entries(shape).filter(([k]) => !keys.includes(k))) as T
}

function updateSchemaFor<K extends RecordApiKind>(kind: K) {
  const editable = omit(RECORD_FIELDS[kind] as Record<string, z.ZodType>, IMMUTABLE_FIELDS[kind])
  return z
    .strictObject(editable)
    .partial()
    .extend({ revision: revisionSchema })
    .refine((v) => Object.keys(v).length > 1, { error: 'Change at least one field.' })
}

/** Create bodies: strict (VAL-002 unknown fields) plus the record-level rules. */
export const recordCreateSchemas = {
  issuers: createSchemaFor('issuers'),
  securities: createSchemaFor('securities'),
  sources: createSchemaFor('sources'),
  incidents: createSchemaFor('incidents'),
  assessments: createSchemaFor('assessments'),
  engagements: createSchemaFor('engagements'),
  objectives: createSchemaFor('objectives'),
  milestones: createSchemaFor('milestones'),
  dialogues: createSchemaFor('dialogues'),
}

/** Update bodies: `revision` plus any editable fields. The API re-validates the merged record. */
export const recordUpdateSchemas = {
  issuers: updateSchemaFor('issuers'),
  securities: updateSchemaFor('securities'),
  sources: updateSchemaFor('sources'),
  incidents: updateSchemaFor('incidents'),
  assessments: updateSchemaFor('assessments'),
  engagements: updateSchemaFor('engagements'),
  objectives: updateSchemaFor('objectives'),
  milestones: updateSchemaFor('milestones'),
  dialogues: updateSchemaFor('dialogues'),
}

export type IssuerInput = z.input<typeof recordCreateSchemas.issuers>
export type SecurityInput = z.input<typeof recordCreateSchemas.securities>
export type SourceInput = z.input<typeof recordCreateSchemas.sources>
export type IncidentInput = z.input<typeof recordCreateSchemas.incidents>
export type AssessmentInput = z.input<typeof recordCreateSchemas.assessments>
export type EngagementInput = z.input<typeof recordCreateSchemas.engagements>
export type ObjectiveInput = z.input<typeof recordCreateSchemas.objectives>
export type MilestoneInput = z.input<typeof recordCreateSchemas.milestones>
export type DialogueInput = z.input<typeof recordCreateSchemas.dialogues>

export type RecordInput<K extends RecordApiKind> = z.input<(typeof recordCreateSchemas)[K]>
export type RecordValues<K extends RecordApiKind> = z.output<(typeof recordCreateSchemas)[K]>

// --- Actions -------------------------------------------------------------------

/** submit/approve/publish/return/retract body. The reason rules live in `reviewTransition`. */
export const reviewActionSchema = z.strictObject({
  revision: revisionSchema,
  reason: z.string({ error: 'Must be text.' }).max(REVIEW_REASON.max).nullable().optional(),
})
export type ReviewActionBody = z.input<typeof reviewActionSchema>

/** D-06 release of the investor-visible fields of an engagement or objective. */
export const releaseSchema = z.strictObject({
  revision: revisionSchema,
  reason: z.string({ error: 'Must be text.' }).max(REVIEW_REASON.max),
})
export type ReleaseBody = z.input<typeof releaseSchema>

/** P-WF-003: a correction starts a new draft version of a published record. */
export const correctionSchema = z.strictObject({
  change_reason: requiredText(REVIEW_REASON),
})
export type CorrectionBody = z.input<typeof correctionSchema>

// --- Queries -------------------------------------------------------------------

export const RECORD_SORTS: Record<RecordApiKind, readonly [string, ...string[]]> = {
  issuers: ['name', 'updated_at', 'country'],
  securities: ['identifier', 'updated_at'],
  sources: ['updated_at', 'retrieved_at', 'title'],
  incidents: ['updated_at', 'event_date', 'title'],
  assessments: ['updated_at', 'version'],
  engagements: ['updated_at', 'due_date', 'title'],
  objectives: ['updated_at', 'due_date', 'title'],
  milestones: ['updated_at', 'assessment_date'],
  dialogues: ['updated_at', 'event_date'],
}

/**
 * `GET /records/:kind`. `q` is a case-insensitive match on the kind's name column (issuer
 * legal name and aliases, titles, security identifiers); `client_org_id=shared` selects
 * records shared with every subscriber (D-03).
 */
export const recordListQuerySchema = pageQuerySchema.extend({
  q: z
    .string()
    .max(120)
    .refine((v) => !hasControlChars(v), { error: 'Control characters are not permitted.' })
    .transform((v) => v.trim())
    .optional(),
  sort: z.string().max(40).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  state: z.enum(REVIEW_STATES.values).optional(),
  client_org_id: z.string().max(64).optional(),
  issuer_id: z.string().max(64).optional(),
  engagement_id: z.string().max(64).optional(),
  objective_id: z.string().max(64).optional(),
  status: z.string().max(40).optional(),
  finding: z.enum(FINDINGS.values).optional(),
  sharing: z.enum(SHARING.values).optional(),
  mapping_state: z.enum(MAPPING_STATES.values).optional(),
})
export type RecordListQuery = z.input<typeof recordListQuerySchema>

// --- Response DTOs (snake_case, ARCHITECTURE §7) -------------------------------

export interface RecordBase {
  id: string
  kind: RecordApiKind
  revision: number
  created_by: string | null
  created_at: string
  updated_at: string
}

/** Review columns shared by reviewed and release-gated kinds (GAP-DD-006/007/009). */
export interface ReviewMeta {
  state: ReviewState
  version: number
  previous_id: string | null
  change_reason: string | null
  submitted_by: string | null
  submitted_at: string | null
  submission_note: string | null
  approved_by: string | null
  approved_at: string | null
  approval_reason: string | null
  published_by: string | null
  published_at: string | null
  publication_reason: string | null
  returned_by: string | null
  returned_at: string | null
  return_reason: string | null
  retracted_by: string | null
  retracted_at: string | null
  retraction_reason: string | null
}

export interface IssuerRecord extends RecordBase {
  kind: 'issuers'
  legal_name: string
  aliases: string[]
  country: string
  sector: string
  status: 'active' | 'inactive'
  description: string | null
  website: string | null
  parent_issuer_id: string | null
  provenance: string
  identifiers: { type: string; value: string; valid_from: string; valid_to: string | null }[]
}

export interface SecurityRecord extends RecordBase {
  kind: 'securities'
  identifier_type: string
  identifier_value: string
  name: string | null
  issuer_id: string | null
  issuer_name: string | null
  mapping_state: string
  mapping_source: string
  effective_from: string
  effective_to: string | null
}

export interface SourceRecord extends RecordBase {
  kind: 'sources'
  client_org_id: string | null
  issuer_id: string | null
  issuer_name: string | null
  title: string
  url: string
  source_class: string
  retrieved_at: string
  publication_date: string | null
  publication_date_precision: string | null
  reporting_period_start: string | null
  reporting_period_end: string | null
  rights_basis: string
  redistribution: string
  licence_ref: string | null
  rights_terms: string | null
  sharing: 'client' | 'internal'
  summary: string
  independence_group: string | null
  provider_id: string | null
  /** Canonical shared evidence (feed-derived): immutable through this API (WF-015). */
  research_version_id: string | null
}

export interface IncidentRecord extends RecordBase {
  kind: 'incidents'
  client_org_id: string | null
  issuer_id: string
  issuer_name: string | null
  title: string
  location: string
  country: string | null
  event_type: string | null
  theme: string | null
  legal_status: string
  description: string
  company_response: string
  uncertainty: string
  entity_role: string | null
  event_date: string | null
  event_date_precision: string | null
  event_identity: string | null
  authority: string | null
  proceeding_reference: string | null
  legal_entity_id: string | null
  status_as_of: string | null
  previous_status_id: string | null
  signal_id: string | null
  source_ids: string[]
}

export interface AssessmentRecord extends RecordBase, ReviewMeta {
  kind: 'assessments'
  client_org_id: string | null
  issuer_id: string
  issuer_name: string | null
  finding: string
  norm: string
  involvement: string
  severity: string
  confidence: string
  remediation: string
  rationale: string
  priority: string
  effective_date: string | null
  incident_ids: string[]
  source_ids: string[]
}

/** The investor-visible copy written at release (D-06). */
export interface EngagementReleased {
  title: string | null
  theme: string | null
  status: string | null
  next_action: string | null
  due_date: string | null
  released_by: string | null
  released_at: string | null
}

export interface EngagementRecord extends RecordBase, ReviewMeta {
  kind: 'engagements'
  client_org_id: string | null
  issuer_id: string
  issuer_name: string | null
  assessment_id: string
  title: string
  theme: string | null
  assigned_manager_id: string
  assigned_reviewer_id: string
  status: string
  next_action: string
  due_date: string
  original_start: string | null
  fruuit_start: string | null
  closure_reason: string | null
  closed_at: string | null
  closed_by: string | null
  released: EngagementReleased | null
  has_unreleased_changes: boolean
}

export interface ObjectiveReleased {
  title: string | null
  baseline: string | null
  change_request: string | null
  next_action: string | null
  due_date: string | null
  released_by: string | null
  released_at: string | null
}

export interface ObjectiveRecord extends RecordBase, ReviewMeta {
  kind: 'objectives'
  client_org_id: string | null
  engagement_id: string
  engagement_title: string | null
  title: string
  category: string | null
  baseline: string
  change_request: string
  owner_id: string
  next_action: string
  due_date: string
  released: ObjectiveReleased | null
  has_unreleased_changes: boolean
}

export interface MilestoneRecord extends RecordBase, ReviewMeta {
  kind: 'milestones'
  client_org_id: string | null
  objective_id: string
  objective_title: string | null
  engagement_id: string
  engagement_title: string | null
  stage: string
  rationale: string
  limitations: string
  assessment_date: string
  evidence_ids: string[]
}

export interface DialogueRecord extends RecordBase, ReviewMeta {
  kind: 'dialogues'
  client_org_id: string | null
  engagement_id: string
  engagement_title: string | null
  event_date: string
  event_type: string
  title: string
  company_response: string
  fruuit_judgment: string
  substantive: boolean
  next_action: string
  due_date: string
  source_ids: string[]
}

export interface RecordByKind {
  issuers: IssuerRecord
  securities: SecurityRecord
  sources: SourceRecord
  incidents: IncidentRecord
  assessments: AssessmentRecord
  engagements: EngagementRecord
  objectives: ObjectiveRecord
  milestones: MilestoneRecord
  dialogues: DialogueRecord
}

export type AnyRecord = RecordByKind[RecordApiKind]

export interface ContributorDto {
  user_id: string
  display_name: string
  first_edited_at: string
  last_edited_at: string
  edit_count: number
}

/** Actions the caller can take now (cosmetic: the server re-checks everything, P-UI-003). */
export const RECORD_ACTIONS = [
  'edit',
  'submit',
  'approve',
  'publish',
  'return',
  'retract',
  'release',
  'correct',
] as const
export type RecordAction = (typeof RECORD_ACTIONS)[number]

/** `GET /records/:kind/:id`: the record plus who worked on it and what the caller may do. */
export type RecordDetail<K extends RecordApiKind = RecordApiKind> = RecordByKind[K] & {
  contributors: ContributorDto[]
  /** Display names for every user id in the record (staff only). */
  people: Record<string, string>
  actions: RecordAction[]
  /** Why review actions are unavailable, e.g. `not_independent` (shown to the caller). */
  blocked_reason: string | null
}

export interface RecordVersionDto {
  id: string
  version: number
  state: ReviewState
  previous_id: string | null
  change_reason: string | null
  created_at: string
  published_at: string | null
  retracted_at: string | null
  revision: number
}

export interface ReviewEventDto {
  id: string
  record_id: string
  record_version: number
  action: string
  from_state: string | null
  to_state: string
  actor_id: string
  actor_name: string | null
  reason: string | null
  created_at: string
}

/** `GET /records/:kind/:id/history`: the version chain (oldest first) and review events. */
export interface RecordHistory {
  kind: RecordApiKind
  id: string
  versions: RecordVersionDto[]
  events: ReviewEventDto[]
}
